import { useState } from "react";

const LS_KEY = "pm26.operator";

export type OperatorRol =
  | "PL"
  | "SC"
  | "EC"
  | "CPC"
  | "PEC"
  | "FM"
  | "anders";

export interface Operator {
  naam: string;
  rol: OperatorRol;
}

/** Geeft de actieve operator terug uit localStorage, of null als niet ingesteld. */
export function getOperator(): Operator | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Operator;
  } catch {
    return null;
  }
}

/** Slaat de operator op in localStorage. */
export function setOperator(op: Operator): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(op));
  } catch {
    /* quota — negeer */
  }
}

/**
 * Geeft "naam (rol)" terug voor gebruik als melder-veld in incidenten,
 * of "onbekend" als er geen operator is ingesteld.
 */
export function getMelderString(): string {
  const op = getOperator();
  if (!op) return "onbekend";
  return `${op.naam} (${op.rol})`;
}

/**
 * Lees URL-params ?operator=Naam&rol=CPC eenmalig uit op module-load,
 * sla op in localStorage en verwijder de params zodat een reload ze negeert.
 */
function applyUrlPrefill(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const naam = params.get("operator");
    if (!naam) return;
    const rolParam = params.get("rol") ?? "";
    const validRollen = new Set([
      "PL",
      "SC",
      "EC",
      "CPC",
      "PEC",
      "FM",
      "anders",
    ]);
    const rol: OperatorRol = validRollen.has(rolParam)
      ? (rolParam as OperatorRol)
      : "anders";
    setOperator({ naam, rol });
    // Verwijder params uit URL zodat een reload ze niet opnieuw verwerkt
    const url = new URL(window.location.href);
    url.searchParams.delete("operator");
    url.searchParams.delete("rol");
    history.replaceState(null, "", url.toString());
  } catch {
    /* SSR / test-omgeving — negeer */
  }
}

applyUrlPrefill();

/**
 * Lichte React-hook die de actieve operator blootstelt met een setter.
 * De setter schrijft naar localStorage en triggert een re-render.
 */
export function useOperator(): [Operator | null, (op: Operator) => void] {
  const [operator, setOperatorState] = useState<Operator | null>(getOperator);

  function setter(op: Operator): void {
    setOperator(op);
    setOperatorState(op);
  }

  return [operator, setter];
}
